/* eslint-disable @typescript-eslint/no-explicit-any, react/no-children-prop */
import { Drawer, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useRef } from "react";
import { FormProvider, FormState } from "../../../components/FormProvider";
import { Child, Family, ServicePeriod } from "../../../models";
import { useSaveServicePeriodMutation } from "../../../redux/apiStore";
import { parseDate } from "../../../utils";
import { EditServicePeriodForm } from "./EditServicePeriodForm";

interface EditServicePeriodDrawerProps {
  open: boolean;
  families: Family[];
  children: Child[];
  servicePeriod: ServicePeriod | null;
  defaultFamilyId?: number;
  onClose: () => void;
  onSaved: () => void;
}

export function EditServicePeriodDrawer(props: EditServicePeriodDrawerProps) {
  const { open, families, children, servicePeriod, defaultFamilyId, onClose, onSaved } = props;
  const formDataGetterRef = useRef<(() => ServicePeriod | null) | null>(null);
  const resetFormRef = useRef<(() => void) | null>(null);
  const [saveServicePeriod] = useSaveServicePeriodMutation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const mode = servicePeriod ? "edit" : "create";

  const handleSave = async (form: FormState) => {
    const period = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();

    if (!period) {
      form.notifyError("Family, start date, and end date are required.");
      return;
    }

    const startDate = parseDate(period.startCentralDateTime);
    const endDate = parseDate(period.endCentralDateTime);
    if (!startDate || !endDate) {
      form.notifyError("Service dates are invalid.");
      return;
    }

    if (startDate > endDate) {
      form.notifyError("Service start date must be on or before service end date.");
      return;
    }

    try {
      await saveServicePeriod(period).unwrap();
      form.notifySuccess("Service period saved");
      onSaved();
      onClose();
    } catch (err: unknown) {
      // Check if this is a duplicate service period error (unique constraint violation)
      let isDuplicateError = false;
      const errorData = typeof err === "object" && err !== null && "data" in err ? (err as any).data : "";
      const errorDataString = String(errorData).toLowerCase();

      if (
        errorDataString.includes("duplicate key") ||
        errorDataString.includes("UX_ServicePeriod_Family_Start_End_Deleted".toLowerCase()) ||
        errorDataString.includes("unique index")
      ) {
        isDuplicateError = true;
      }

      if (isDuplicateError) {
        const family = families.find((f) => f.id === period.familyId);
        const familyName = family?.name ?? "this family";
        const startDate = new Date(period.startCentralDateTime);
        const endDate = new Date(period.endCentralDateTime);
        const dateRange = `${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`;
        form.notifyError(
          `A service period already exists for ${familyName} family from ${dateRange}. Please choose different dates or edit the existing service period.`,
        );
      } else {
        form.notifyError("Error saving service period", err);
      }
    }
  };

  const handleCancel = async (form: FormState) => {
    try {
      resetFormRef.current?.();
      onClose();
    } catch (err) {
      form.notifyError("Error undoing changes", err);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: isSmallScreen ? "100%" : "380px",
            maxWidth: "100%",
            zIndex: 1550,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Stack sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <FormProvider fullHeight onSave={handleSave} onCancel={handleCancel} onDelete={() => {}} showControls={false}>
          <EditServicePeriodForm
            mode={mode}
            families={families}
            children={children}
            servicePeriod={servicePeriod}
            defaultFamilyId={defaultFamilyId}
            dataGetterRef={formDataGetterRef}
            resetFormRef={resetFormRef}
          />
        </FormProvider>
      </Stack>
    </Drawer>
  );
}
